import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import StorageIcon from '@mui/icons-material/Storage';
import WidgetsIcon from '@mui/icons-material/Widgets';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useMemo } from 'react';
import { EntityType } from '../entities/entityType';
import AddEntityDialog from './addEntityDialog';

type Props = {
  blueprints: Array<{ label: string; value: string }>;
  onCreateFromBlueprint: (blueprintId: string) => void;
  onCreateAgent: (agentDefId: string, entityInstanceId?: string) => void;
  onCreateUserApp: (appDefId: string, entityInstanceId?: string) => void;
  onCreateDataSource: (dataSourceDefId: string, entityInstanceId?: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
};

const LeftSidebar = ({
  blueprints,
  onCreateFromBlueprint,
  onCreateAgent,
  onCreateUserApp,
  onCreateDataSource,
  collapsed,
  onToggleCollapse,
}: Props) => {
  const [entityType, setEntityType] = useState<EntityType>();
  const [openAddEntityDialog, setOpenAddEntityDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [openBlueprintMenu, setOpenBlueprintMenu] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredBlueprints = useMemo(() => {
    return blueprints.filter((t) => t.label.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [blueprints, searchQuery]);

  const toolItems = useMemo(
    () => [
      {
        type: EntityType.dataSourceInstance,
        label: 'Data Source',
        secondary: 'Add a data source',
        icon: <StorageIcon color="primary" />,
      },
      {
        type: EntityType.agentInstance,
        label: 'Agent',
        secondary: 'Add an agent',
        icon: <SplitscreenIcon color="primary" />,
      },
      {
        type: EntityType.userAppInstance,
        label: 'App',
        secondary: 'Add an app',
        icon: <WidgetsIcon color="primary" />,
      },
    ],
    [],
  );

  const filteredToolItems = useMemo(() => {
    return toolItems.filter(
      (item) =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.secondary.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [toolItems, searchQuery]);

  const showBlueprintMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
    setOpenBlueprintMenu(true);
  };

  const showAddEntityDialog = (entityType: EntityType) => {
    setEntityType(entityType);
    setOpenAddEntityDialog(true);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setOpenBlueprintMenu(false);
  };

  const handleCreateFromBlueprint = async (blueprintId: string) => {
    onCreateFromBlueprint(blueprintId);
    closeMenu();
  };

  if (collapsed) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: 60,
          height: '100%',
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack spacing={1} sx={{ p: 1 }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton onClick={onToggleCollapse} size="small">
              <ChevronLeftIcon sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>
          </Tooltip>
          <Divider />
          <Tooltip title="Create from blueprint" placement="right">
            <IconButton onClick={showBlueprintMenu} color="primary">
              <AccountTreeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add data source" placement="right">
            <IconButton onClick={() => showAddEntityDialog(EntityType.dataSourceInstance)} color="primary">
              <StorageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add agent" placement="right">
            <IconButton onClick={() => showAddEntityDialog(EntityType.agentInstance)} color="primary">
              <SplitscreenIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add app" placement="right">
            <IconButton onClick={() => showAddEntityDialog(EntityType.userAppInstance)} color="primary">
              <WidgetsIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {openBlueprintMenu && (
          <Menu anchorEl={menuAnchorEl} open={openBlueprintMenu} onClose={closeMenu}>
            {blueprints.map((option, idx) => (
              <MenuItem key={idx} dense onClick={() => handleCreateFromBlueprint(option.value)}>
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        )}

        {openAddEntityDialog && entityType && (
          <AddEntityDialog
            entityType={entityType}
            open={openAddEntityDialog}
            onCancel={() => setOpenAddEntityDialog(false)}
            onSubmit={(entityDefId: string, entityInstanceId?: string) => {
              setOpenAddEntityDialog(false);
              if (entityType === EntityType.dataSourceInstance) {
                onCreateDataSource(entityDefId, entityInstanceId);
              } else if (entityType === EntityType.agentInstance) {
                onCreateAgent(entityDefId, entityInstanceId);
              } else if (entityType === EntityType.userAppInstance) {
                onCreateUserApp(entityDefId, entityInstanceId);
              }
            }}
          />
        )}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        height: '100%',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Tools
        </Typography>
        <Tooltip title="Collapse sidebar">
          <IconButton onClick={onToggleCollapse} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search tools..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              bgcolor: 'action.hover',
              '& fieldset': { border: 'none' },
            },
          }}
        />
      </Box>

      <List sx={{ flexGrow: 1, overflow: 'auto' }}>
        {filteredBlueprints.length > 0 && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={showBlueprintMenu}>
                <ListItemIcon>
                  <AccountTreeIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Create from Blueprint" />
              </ListItemButton>
            </ListItem>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {filteredToolItems.length > 0 && (
          <>
            <ListItem>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Add Components
              </Typography>
            </ListItem>

            {filteredToolItems.map((item) => (
              <ListItem disablePadding key={item.type}>
                <ListItemButton onClick={() => showAddEntityDialog(item.type)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} secondary={item.secondary} />
                </ListItemButton>
              </ListItem>
            ))}
          </>
        )}

        {filteredBlueprints.length === 0 && filteredToolItems.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No results found for "{searchQuery}"
            </Typography>
          </Box>
        )}
      </List>

      {openBlueprintMenu && (
        <Menu anchorEl={menuAnchorEl} open={openBlueprintMenu} onClose={closeMenu}>
          {blueprints.map((option, idx) => (
            <MenuItem key={idx} dense onClick={() => handleCreateFromBlueprint(option.value)}>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      )}

      {openAddEntityDialog && entityType && (
        <AddEntityDialog
          entityType={entityType}
          open={openAddEntityDialog}
          onCancel={() => setOpenAddEntityDialog(false)}
          onSubmit={(entityDefId: string, entityInstanceId?: string) => {
            setOpenAddEntityDialog(false);
            if (entityType === EntityType.dataSourceInstance) {
              onCreateDataSource(entityDefId, entityInstanceId);
            } else if (entityType === EntityType.agentInstance) {
              onCreateAgent(entityDefId, entityInstanceId);
            } else if (entityType === EntityType.userAppInstance) {
              onCreateUserApp(entityDefId, entityInstanceId);
            }
          }}
        />
      )}
    </Paper>
  );
};

export default LeftSidebar;
